import { Box, createStyles, Group, Modal } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { Button, Icon, Text, Title } from '@repo/foundations';
import { Link, useLocation, useNavigate } from 'react-router';
import { NavBarBreadcrumbWrapper, NavBarWrapper } from '.';
import { useFeatureFlags } from '../../../utils/featureFlags';

const useStyles = createStyles((theme) => ({
	backToButton: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing['3xs'],
		textDecoration: 'none',
		borderRadius: theme.other.borderRadius.sm,
		height: theme.other.space[7],
		width: '100%',
		cursor: 'pointer',
		color: theme.other.getColor('text/primary/default'),
		backgroundColor: 'transparent',

		':hover': {
			backgroundColor: theme.other.getColor('fill/transparent/hover'),
		},
		':active': {
			backgroundColor: theme.other.getColor('fill/transparent/active'),
		},
	},
}));

const NOTIFICATION_PATHS = [
	'/notifications/app',
	'/notifications/email',
	'/notifications/slack',
];
const TEMPLATES_PATH = '/templates/manage';
const BACK_BUTTON_PATHS = [TEMPLATES_PATH, ...NOTIFICATION_PATHS];

function SettingsPageNavBar() {
	const { theme, classes } = useStyles();
	const location = useLocation();
	const navigate = useNavigate();
	const { settingsNav } = useFeatureFlags();
	const [opened, { open, close }] = useDisclosure(false);

	const isTemplatesPage = location.pathname.includes(TEMPLATES_PATH);
	const isNotificationsPage = NOTIFICATION_PATHS.some((path) =>
		location.pathname.includes(path)
	);

	const showBackButton = BACK_BUTTON_PATHS.some((path) =>
		location.pathname.includes(path)
	);
	const backButtonMsg = isTemplatesPage ? 'Templates' : 'Notifications';
	const backButtonTo = isTemplatesPage
		? '/settings/templates'
		: '/settings/notifications';

	const handleBackButton = (e: React.MouseEvent) => {
		if (isNotificationsPage) {
			e.preventDefault();
			navigate('/settings/notifications');
			return;
		}
		if (isTemplatesPage) {
			const isFormDirty =
				sessionStorage.getItem('templateFormDirty') === 'true';
			if (isFormDirty) {
				e.preventDefault();
				open();
			}
		}
	};

	return (
		<NavBarWrapper>
			<Group>
				{!settingsNav && (
					<NavBarBreadcrumbWrapper>
						<Title
							style={{ width: '240px' }}
							order={1}
							size="sm"
							data-testid="settings-page-nav-bar-title"
						>
							Settings
						</Title>
					</NavBarBreadcrumbWrapper>
				)}
				{showBackButton && (
					<Box>
						<Link
							className={classes.backToButton}
							to={backButtonTo}
							onClick={handleBackButton}
						>
							<Icon name="chevronLeft" />
							<Text
								fz="sm"
								weight="semibold"
								style={{ paddingRight: theme.spacing.xs }}
							>
								{backButtonMsg}
							</Text>
						</Link>
					</Box>
				)}
			</Group>

			<Modal
				opened={opened}
				onClose={close}
				title="Leave page with unsaved changes?"
			>
				<Text>Changes you made may not be saved.</Text>
				<Group position="right" mt={12}>
					<Button onClick={close}>Stay</Button>
					<Button
						variant="primary"
						onClick={() => {
							navigate('/settings/templates');
							close();
						}}
					>
						Leave
					</Button>
				</Group>
			</Modal>
		</NavBarWrapper>
	);
}

export default SettingsPageNavBar;
